import { useState } from 'react';
import { type Language, t } from '../i18n';

interface LeadCaptureProps {
  language: Language;
  onSubmit: (lead: Lead) => void;
  isLoading?: boolean;
  isAfterChat?: boolean;
  assessmentType?: 'automation' | 'startup' | 'project';
  // Pre-filled from anything captured earlier (intake form, mid-chat extraction) so
  // the user doesn't retype what they've already shared.
  initialLead?: Partial<Lead>;
}

export interface Lead {
  name: string;
  email: string;
  company?: string;
  phone?: string;
}

export function LeadCapture({
  language,
  onSubmit,
  isLoading,
  isAfterChat: _isAfterChat,
  assessmentType = 'automation',
  initialLead,
}: LeadCaptureProps) {
  const [formData, setFormData] = useState<Lead>({
    name: initialLead?.name ?? '',
    email: initialLead?.email ?? '',
    company: initialLead?.company ?? '',
    phone: initialLead?.phone ?? '',
  });
  const [errors, setErrors] = useState<{ name?: boolean; email?: boolean }>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: { name?: boolean; email?: boolean } = {};
    if (!formData.name.trim()) newErrors.name = true;
    if (!formData.email.trim() || !formData.email.includes('@')) newErrors.email = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  const isItalian = language === 'it';

  return (
    <div className="max-w-md mx-auto">
      <div className="card text-center mb-8">
        <div className="w-20 h-20 bg-green-500/10 dark:bg-green-400/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-10 h-10 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {isItalian ? 'Valutazione Completata!' : 'Assessment Complete!'}
        </h2>
        <p className="text-muted-foreground">
          {isItalian
            ? assessmentType === 'startup'
              ? 'Inserisci i tuoi dati per ricevere il report personalizzato con l\'analisi della tua idea di startup.'
              : assessmentType === 'project'
                ? 'Inserisci i tuoi dati per ricevere il report personalizzato con l\'analisi tecnica del tuo progetto.'
                : 'Inserisci i tuoi dati per ricevere il report personalizzato con le opportunità di automazione.'
            : assessmentType === 'startup'
              ? 'Enter your details to receive your personalized startup analysis report.'
              : assessmentType === 'project'
                ? 'Enter your details to receive your personalized project technical analysis report.'
                : 'Enter your details to receive your personalized report with automation opportunities.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1">
            {isItalian ? 'Nome e Cognome' : 'Full Name'} *
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, name: e.target.value }));
              setErrors((prev) => ({ ...prev, name: false }));
            }}
            className={`form-input ${errors.name ? 'border-destructive' : ''}`}
            placeholder={isItalian ? 'Mario Rossi' : 'John Smith'}
            disabled={isLoading}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-destructive">
              {isItalian ? 'Campo obbligatorio' : 'Required field'}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
            Email *
          </label>
          <input
            type="email"
            id="email"
            value={formData.email}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, email: e.target.value }));
              setErrors((prev) => ({ ...prev, email: false }));
            }}
            className={`form-input ${errors.email ? 'border-destructive' : ''}`}
            placeholder="email@azienda.it"
            disabled={isLoading}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-destructive">
              {isItalian ? 'Email non valida' : 'Invalid email'}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="company" className="block text-sm font-medium text-foreground mb-1">
            {isItalian ? 'Azienda' : 'Company'}
          </label>
          <input
            type="text"
            id="company"
            value={formData.company}
            onChange={(e) => setFormData((prev) => ({ ...prev, company: e.target.value }))}
            className="form-input"
            placeholder={isItalian ? 'Nome azienda' : 'Company name'}
            disabled={isLoading}
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-1">
            {isItalian ? 'Telefono' : 'Phone'}
          </label>
          <input
            type="tel"
            id="phone"
            value={formData.phone}
            onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
            className="form-input"
            placeholder="+39 333 1234567"
            disabled={isLoading}
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full mt-6"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </span>
              {isItalian ? 'Generazione report...' : 'Generating report...'}
            </span>
          ) : (
            t(language, 'chat.viewReport')
          )}
        </button>

        <p className="text-xs text-muted-foreground text-center mt-4">
          {isItalian
            ? 'I tuoi dati saranno trattati secondo la nostra privacy policy.'
            : 'Your data will be handled according to our privacy policy.'}
        </p>
      </form>
    </div>
  );
}
